import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import { SITE_URL } from '../config/seo';
import api from '../utils/api';
import { getCityFromHost } from '../utils/getCityFromHost';
import Pagination from '../components/Pagination';
import { stripHtmlTags } from '../utils/htmlUtils';
import { showSuccess, showError } from '../utils/toast';
import { isUserLoggedIn } from '../utils/auth';
import FavouriteButton from '../components/FavouriteButton';
import BusinessCard from '../components/BusinessCard';
import * as LucideIcons from 'lucide-react';

const TagPage = () => {
  const { tagSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [tag, setTag] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const city = getCityFromHost();

  const categorySlug = searchParams.get('category');
  const subcategorySlug = searchParams.get('subcategory');
  const searchQuery = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page')) || 1;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // First fetch tag to get its ID
        const tagResponse = await api.get(`/tags/${tagSlug}`);
        const tagData = tagResponse.data;
        setTag(tagData);

        // Build businesses API URL with filters
        let businessesUrl = `/businesses/tag/${tagSlug}`;
        const queryParams = [];
        if (categorySlug) queryParams.push(`category=${categorySlug}`);
        if (subcategorySlug) queryParams.push(`subcategory=${subcategorySlug}`);
        if (searchQuery) queryParams.push(`search=${encodeURIComponent(searchQuery)}`);
        queryParams.push(`page=${currentPage}`);
        if (queryParams.length > 0) {
          businessesUrl += `?${queryParams.join('&')}`;
        }

        // Then fetch all other data in parallel
        const [
          businessesResponse,
          tagsResponse,
          categoriesResponse,
          subcategoriesResponse
        ] = await Promise.all([
          api.get(businessesUrl),
          api.get('/tags'),
          api.get('/categories'),
          categorySlug
            ? api.get(`/subcategories?categoryId=${categorySlug}`).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] })
        ]);

        const subcategoriesData = subcategoriesResponse.data || [];
        const tagsData = tagsResponse.data || [];
        const categoriesData = categoriesResponse.data || [];

        // Handle paginated response
        if (businessesResponse.data && businessesResponse.data.businesses) {
          setBusinesses(businessesResponse.data.businesses);
          setPagination(businessesResponse.data.pagination);
        } else if (Array.isArray(businessesResponse.data)) {
          // Fallback for non-paginated response (backward compatibility)
          setBusinesses(businessesResponse.data);
          setPagination(null);
        } else {
          setBusinesses([]);
          setPagination(null);
        }
        setAllTags(tagsData);
        setCategories(categoriesData);
        setSubcategories(subcategoriesData);

        // Set selected category and subcategory
        if (categorySlug) {
          const cat = categoriesData.find(c => c.slug === categorySlug);
          setSelectedCategory(cat || null);

          if (subcategorySlug && cat) {
            const subcat = subcategoriesData.find(s => s.slug === subcategorySlug);
            setSelectedSubcategory(subcat || null);
          } else {
            setSelectedSubcategory(null);
          }
        } else {
          setSelectedCategory(null);
          setSelectedSubcategory(null);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tagSlug, categorySlug, subcategorySlug, currentPage, searchQuery]);

  useEffect(() => {
    if (!isUserLoggedIn()) return;
    api.get('/favourites').then((r) => {
      const list = r.data?.favourites || [];
      setFavouriteIds(new Set(list.map((b) => (b._id || b).toString())));
    }).catch(() => { });
  }, []);

  const handleAddFavourite = async (id) => {
    try {
      await api.post(`/favourites/${id}`);
      setFavouriteIds((prev) => new Set([...prev, id.toString()]));
      showSuccess('Added to favourites');
    } catch (e) {
      showError(e.response?.data?.message || 'Could not add to favourites');
    }
  };
  const handleRemoveFavourite = async (id) => {
    try {
      await api.delete(`/favourites/${id}`);
      setFavouriteIds((prev) => {
        const next = new Set(prev);
        next.delete(id.toString());
        return next;
      });
      showSuccess('Removed from favourites');
    } catch (e) {
      showError(e.response?.data?.message || 'Could not remove from favourites');
    }
  };

  const cityName = city ? city.charAt(0).toUpperCase() + city.slice(1) : '';
  const locationName = cityName || 'Fatehabad';

  const canonical = `/tag/${tagSlug || ''}`;
  const breadcrumbLd = tag ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: `Tag: ${tag.name}`, item: `${SITE_URL}${canonical}` },
      ...(selectedCategory ? [{ '@type': 'ListItem', position: 3, name: selectedCategory.name, item: `${SITE_URL}${canonical}?category=${selectedCategory.slug}` }] : []),
      ...(selectedSubcategory ? [{ '@type': 'ListItem', position: 4, name: selectedSubcategory.name, item: `${SITE_URL}${canonical}?category=${selectedCategory?.slug || ''}&subcategory=${selectedSubcategory.slug}` }] : []),
    ].slice(0, 4),
  } : null;

  return (
    <>
      <SEO
        title={tag?.metaTitle || (tag ? `${tag.name} Businesses in ${locationName}` : 'Tag')}
        description={tag?.metaDescription || (tag ? `Find businesses tagged with ${tag.name} in ${locationName}. Browse listings, contact information, and more.` : 'Browse businesses by tag')}
        canonical={canonical || undefined}
        jsonLd={breadcrumbLd ? [breadcrumbLd] : undefined}
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="container-custom py-6 md:py-8 lg:py-12">
          {loading ? (
            <PageLoader message="Loading businesses..." />
          ) : tag ? (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
              {/* Main Content - Left Side */}
              <div className="lg:col-span-3">
                <div className="mb-6 md:mb-8">
                  <div className="flex flex-wrap items-center gap-2 mb-3 md:mb-4 text-xs md:text-sm">
                    <Link to="/" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                      Home
                    </Link>
                    <span className="text-gray-400 dark:text-gray-500">/</span>
                    <span className="text-gray-900 dark:text-gray-50 font-medium">Tag: {tag.name}</span>
                    {selectedCategory && (
                      <>
                        <span className="text-gray-400 dark:text-gray-500">/</span>
                        <span className="text-gray-700 dark:text-gray-300">{selectedCategory.name}</span>
                      </>
                    )}
                    {selectedSubcategory && (
                      <>
                        <span className="text-gray-400 dark:text-gray-500">/</span>
                        <span className="text-gray-700 dark:text-gray-300">{selectedSubcategory.name}</span>
                      </>
                    )}
                  </div>
                  <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-900/20 rounded-lg p-4 mb-4 border border-primary-200 dark:border-primary-800">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-gray-900 dark:text-gray-50 mb-1">
                      {tag.name} in {locationName}
                      {selectedCategory && <span className="text-gray-600 dark:text-gray-400"> - {selectedCategory.name}</span>}
                      {selectedSubcategory && <span className="text-gray-600 dark:text-gray-400"> - {selectedSubcategory.name}</span>}
                    </h1>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm md:text-base">
                    {pagination?.totalItems || businesses.length} {(pagination?.totalItems || businesses.length) === 1 ? 'business' : 'businesses'} in {locationName}
                  </p>
                  <div className="mb-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={localSearchTerm}
                        onChange={(e) => setLocalSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const newParams = new URLSearchParams(searchParams);
                            if (localSearchTerm.trim()) {
                              newParams.set('search', localSearchTerm.trim());
                            } else {
                              newParams.delete('search');
                            }
                            newParams.delete('page');
                            setSearchParams(newParams);
                          }
                        }}
                        placeholder="Search within this tag..."
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        onClick={() => {
                          const newParams = new URLSearchParams(searchParams);
                          if (localSearchTerm.trim()) {
                            newParams.set('search', localSearchTerm.trim());
                          } else {
                            newParams.delete('search');
                          }
                          newParams.delete('page');
                          setSearchParams(newParams);
                        }}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        Search
                      </button>
                      {searchQuery && (
                        <button
                          onClick={() => {
                            setLocalSearchTerm('');
                            const newParams = new URLSearchParams(searchParams);
                            newParams.delete('search');
                            newParams.delete('page');
                            setSearchParams(newParams);
                          }}
                          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {searchQuery && (
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Showing results for: <span className="font-semibold">"{searchQuery}"</span>
                      </p>
                    )}
                  </div>
                  {(selectedCategory || selectedSubcategory) && (
                    <Link
                      to={`/tag/${tagSlug}`}
                      className="inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Clear Filters
                    </Link>
                  )}
                </div>

                {businesses.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-3 gap-0 md:gap-6 divide-y md:divide-y-0 divide-gray-100 dark:divide-gray-800">
                    {businesses.map((business) => (
                      <BusinessCard
                        key={business._id}
                        business={business}
                        favouriteIds={favouriteIds}
                        onAddFavourite={handleAddFavourite}
                        onRemoveFavourite={handleRemoveFavourite}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="card p-8 md:p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg mb-2">
                      {selectedCategory || selectedSubcategory
                        ? 'No businesses found with the selected filters.'
                        : 'No businesses found with this tag yet.'}
                    </p>
                    {(selectedCategory || selectedSubcategory) && (
                      <Link
                        to={`/tag/${tagSlug}`}
                        className="inline-block mt-4 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
                      >
                        Clear Filters
                      </Link>
                    )}
                  </div>
                )}

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    itemsPerPage={pagination.itemsPerPage}
                    itemsOnCurrentPage={pagination.itemsOnCurrentPage}
                    hasNextPage={pagination.hasNextPage}
                    hasPrevPage={pagination.hasPrevPage}
                    nextPage={pagination.nextPage}
                    prevPage={pagination.prevPage}
                    baseUrl={`/tag/${tagSlug}`}
                    queryParams={{
                      ...(categorySlug && { category: categorySlug }),
                      ...(subcategorySlug && { subcategory: subcategorySlug })
                    }}
                  />
                )}
              </div>

              {/* Sidebar - Right Side */}
              <div className="lg:col-span-1">
                <div className="space-y-4">
                  {/* Related Tags */}
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-800 shadow-sm">
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-50 mb-3 md:mb-4">Related Tags</h3>
                    <div className="space-y-2">
                      {allTags
                        .filter(t => t.slug !== tagSlug)
                        .slice(0, 8)
                        .map((t) => (
                          <Link
                            key={t._id}
                            to={`/tag/${t.slug}`}
                            className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors py-2 text-sm border-b border-gray-100 dark:border-gray-700 last:border-0"
                          >
                            {t.name}
                          </Link>
                        ))}
                    </div>
                    {allTags.length > 8 && (
                      <Link
                        to="/"
                        className="block text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-4 text-sm font-semibold transition-colors"
                      >
                        View All Tags →
                      </Link>
                    )}
                  </div>

                  {/* Categories Filter */}
                  {categories.length > 0 && (
                    <div className="bg-white dark:bg-gray-900 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-800 shadow-sm">
                      <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-50 mb-3 md:mb-4">Filter by Category</h3>
                      <div className="space-y-1">
                        <Link
                          to={`/tag/${tagSlug}${subcategorySlug ? `?subcategory=${subcategorySlug}` : ''}`}
                          className={`block py-2 px-3 rounded-lg text-sm transition-colors ${!selectedCategory
                            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                        >
                          All Categories
                        </Link>
                        {categories.map((cat) => {
                          const IconComponent = cat.icon ? (LucideIcons[cat.icon] || LucideIcons.Folder) : LucideIcons.Folder;
                          return (
                            <Link
                              key={cat._id}
                              to={`/tag/${tagSlug}?category=${cat.slug}${subcategorySlug ? `&subcategory=${subcategorySlug}` : ''}`}
                              className={`block py-2 px-3 rounded-lg text-sm transition-colors flex items-center gap-2 ${selectedCategory?._id === cat._id
                                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                            >
                              <IconComponent className="w-4 h-4 flex-shrink-0" />
                              {cat.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Subcategories Filter */}
                  {subcategories.length > 0 && (
                    <div className="bg-white dark:bg-gray-900 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-800 shadow-sm">
                      <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-50 mb-3 md:mb-4">Subcategories</h3>
                      <div className="space-y-1">
                        <Link
                          to={`/tag/${tagSlug}${categorySlug ? `?category=${categorySlug}` : ''}`}
                          className={`block py-2 px-3 rounded-lg text-sm transition-colors ${!selectedSubcategory
                            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                        >
                          All Subcategories
                        </Link>
                        {subcategories.map((subcat) => (
                          <Link
                            key={subcat._id}
                            to={`/tag/${tagSlug}?category=${categorySlug || ''}&subcategory=${subcat.slug}`}
                            className={`block py-2 px-3 rounded-lg text-sm transition-colors ${selectedSubcategory?._id === subcat._id
                              ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                          >
                            {subcat.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400 text-lg">Tag not found.</p>
              <Link to="/" className="text-primary-600 dark:text-primary-400 hover:underline mt-4 inline-block">
                ← Back to Home
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TagPage;
